import { bootstrapActionTypes } from '@makemydeal/dr-shared-store';
import type { ScopeCreationFactory } from '@makemydeal/shared-scoped-store';
import type { BaggageEntry } from '@makemydeal/dr-dash-bff-types';
import { getActiveComparison, getOrderForTemplateScenariosByIndex, getOrderFromRetrievedScenarios } from './multiScenarioUtils';
import { getGlobalFeatureToggle } from '@makemydeal/dr-shared-ui-utils';

/**
 * Factory that creates uninitialized scopes for alternate scenarios when
 * BOOTSTRAP_SUCCESS is processed by the reducer. This enables skeleton loaders
 * to render immediately — without requiring a separate dispatch.
 *
 * Only active when the V2 async multi-scenario load path is enabled.
 */
export const dashScopeCreationFactory: ScopeCreationFactory = (action, currentOrder, primaryScopeId) => {
    if (action.type !== bootstrapActionTypes.BOOTSTRAP_SUCCESS) {
        return null;
    }

    if (!getGlobalFeatureToggle('enableMVMSLoadAsync')) {
        return null;
    }

    const baggage = action.payload?.baggage;
    if (!Array.isArray(baggage)) {
        return null;
    }

    const comparisonBaggage = baggage.find(({ name }: BaggageEntry) => name === 'deal360.comparison');
    if (!comparisonBaggage) {
        return null;
    }

    const comparisonList = comparisonBaggage.value.content;
    const dealExchangeVersionId = action.payload.offer.dealExchangeVersionId;
    const activeComparison = getActiveComparison(comparisonList, dealExchangeVersionId);

    if (!activeComparison || activeComparison.scenarios.length <= 1) {
        return null;
    }

    const resolvedPrimaryScopeId = primaryScopeId || currentOrder[0];

    // Template scenarios: reserve scope slots by position (scenarioIndex) instead of by id.
    const primaryScenarioIndex = action.payload.template?.scenarioIndex;
    if (Number.isFinite(primaryScenarioIndex)) {
        return getOrderForTemplateScenariosByIndex(activeComparison.scenarios.length, primaryScenarioIndex, resolvedPrimaryScopeId);
    }

    const nonPrimaryScenarioIds = activeComparison.scenarios.filter(({ isPrimary }: any) => !isPrimary).map(({ id }: any) => id);

    return getOrderFromRetrievedScenarios(activeComparison, nonPrimaryScenarioIds, resolvedPrimaryScopeId);
};
